import { useState } from "react";
import { Sparkles, Loader2, Bookmark, Crown } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useLottery, LOTTERIES } from "@/hooks/use-lottery-selection";
import { usePlanLimits } from "@/hooks/use-plan-limits";
import { toast } from "@/hooks/use-toast";
import { CheckScopeDialog } from "@/components/app/CheckScopeDialog";
import type { SavedGameScope } from "@/lib/check-scope";

type SuggestedGame = { numbers: number[]; explanation: string };

interface Props {
  numbersPerGame: number;
  children?: React.ReactNode;
}

export function AiGeneratorDialog({ numbersPerGame, children }: Props) {
  const { user } = useAuth();
  const { selected: lotterySlug } = useLottery();
  const { has } = usePlanLimits();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(5);
  const [hint, setHint] = useState("");
  const [result, setResult] = useState<{ games: SuggestedGame[]; summary: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedIdx, setSelectedIdx] = useState<Set<number>>(new Set());
  const [savingBulk, setSavingBulk] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);

  const isPremium = has("premium");
  const lotteryName = LOTTERIES.find((l) => l.slug === lotterySlug)?.name;

  const generate = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    setSelectedIdx(new Set());
    try {
      const { data, error: invokeError } = await supabase.functions.invoke("ai-generate-games", {
        body: { lotterySlug, numbersPerGame, gameCount: count, hint },
      });
      if (invokeError) throw new Error(invokeError.message || "Erro ao gerar com IA.");
      if ((data as { error?: string })?.error) throw new Error((data as { error: string }).error);
      const r = data as { games: SuggestedGame[]; summary: string };
      setResult(r);
      // pré-seleciona todos
      setSelectedIdx(new Set(r.games.map((_, i) => i)));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erro inesperado.");
    } finally {
      setLoading(false);
    }
  };

  const toggleOne = (i: number) =>
    setSelectedIdx((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  const toggleAll = () => {
    if (!result) return;
    if (selectedIdx.size === result.games.length) setSelectedIdx(new Set());
    else setSelectedIdx(new Set(result.games.map((_, i) => i)));
  };

  const saveSelected = async (scopeInfo: { scope: SavedGameScope; from: number | null; until: number | null; specialScheduleId?: string | null }) => {
    if (!user || !result || selectedIdx.size === 0) return;
    setSavingBulk(true);
    try {
      const { data: lot } = await supabase.from("lotteries").select("id").eq("slug", lotterySlug).maybeSingle();
      if (!lot) throw new Error("Loteria não encontrada.");
      const rows = result.games
        .map((g, i) => ({ g, i }))
        .filter(({ i }) => selectedIdx.has(i))
        .map(({ g }, idx) => ({
          user_id: user.id,
          lottery_id: lot.id,
          numbers: g.numbers,
          label: `Gerado por IA #${idx + 1}`,
          cost: 0,
          check_scope: scopeInfo.scope,
          check_from_contest: scopeInfo.from,
          check_until_contest: scopeInfo.until,
        special_schedule_id: scopeInfo.specialScheduleId ?? null,
        }));
      const { error } = await supabase.from("saved_games").insert(rows);
      if (error) throw error;
      toast({ title: `${rows.length} jogo(s) salvo(s)`, description: "Veja em Meus jogos." });
      setSelectedIdx(new Set());
    } catch (e) {
      toast({
        title: "Erro ao salvar",
        description: e instanceof Error ? e.message : "Erro inesperado.",
        variant: "destructive",
      });
    } finally {
      setSavingBulk(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children ?? (
          <Button variant="outline" className="border-primary/40 bg-primary/10 text-primary hover:bg-primary/20">
            <Sparkles className="mr-1.5 h-4 w-4" /> Gerar com IA
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-2xl font-light">
            <Sparkles className="h-5 w-5 text-primary" />
            Gerador IA
            <Badge variant="outline" className="border-primary/40 bg-primary/10 text-[10px] uppercase text-primary">
              <Crown className="mr-1 h-3 w-3" /> Premium
            </Badge>
          </DialogTitle>
          <DialogDescription>
            A IA analisa seu histórico de jogos e os últimos 100 sorteios para sugerir apostas balanceadas — cada
            uma com justificativa.
          </DialogDescription>
        </DialogHeader>

        {!isPremium ? (
          <div className="rounded-lg border border-primary/40 bg-primary/5 p-4 text-sm text-foreground">
            Esta funcionalidade é exclusiva do plano Premium.{" "}
            <a href="/planos" className="text-primary underline">Conheça o Premium</a>.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[120px_1fr]">
              <div className="space-y-1.5">
                <Label htmlFor="ai-count" className="text-xs">Qtd. jogos</Label>
                <Input
                  id="ai-count"
                  type="number"
                  min={1}
                  max={10}
                  value={count}
                  onChange={(e) => setCount(Math.min(10, Math.max(1, Number(e.target.value) || 1)))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ai-hint" className="text-xs">Instrução opcional</Label>
                <Input
                  id="ai-hint"
                  placeholder='Ex: "priorize dezenas atrasadas" ou deixe vazio'
                  value={hint}
                  onChange={(e) => setHint(e.target.value)}
                  maxLength={200}
                />
              </div>
            </div>

            {error && (
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}

            {result && (
              <div className="space-y-3">
                {result.summary && (
                  <p className="rounded-lg border border-lt-border bg-lt-bg2 px-3 py-2 text-[13px] italic leading-relaxed text-muted-foreground">
                    {result.summary}
                  </p>
                )}
                <div className="flex items-center justify-between rounded-md border border-lt-border bg-lt-bg3 px-3 py-2">
                  <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                    <Checkbox
                      checked={selectedIdx.size === result.games.length}
                      onCheckedChange={toggleAll}
                    />
                    Selecionar todos ({selectedIdx.size}/{result.games.length})
                  </label>
                  <Button
                    size="sm"
                    onClick={() => setScopeOpen(true)}
                    disabled={savingBulk || selectedIdx.size === 0}
                  >
                    {savingBulk ? (
                      <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Bookmark className="mr-1 h-3.5 w-3.5" />
                    )}
                    Salvar selecionados
                  </Button>
                </div>
                <div className="max-h-[50vh] space-y-3 overflow-y-auto pr-1">
                  {result.games.map((g, i) => {
                    const checked = selectedIdx.has(i);
                    return (
                      <label
                        key={i}
                        className={`flex cursor-pointer gap-3 rounded-lg border p-3 transition-colors ${
                          checked ? "border-primary/50 bg-primary/5" : "border-lt-border bg-background"
                        }`}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleOne(i)}
                          className="mt-1"
                        />
                        <div className="flex-1 space-y-2">
                          <div className="flex flex-wrap items-center gap-1.5">
                            {g.numbers.map((n) => (
                              <span
                                key={n}
                                className="flex h-8 w-8 items-center justify-center rounded-full border border-primary/40 bg-primary/10 text-xs font-medium text-primary"
                              >
                                {String(n).padStart(2, "0")}
                              </span>
                            ))}
                          </div>
                          <p className="text-[13px] leading-relaxed text-muted-foreground">{g.explanation}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Fechar</Button>
          {isPremium && (
            <Button onClick={generate} disabled={loading}>
              {loading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}
              {result ? "Gerar novamente" : "Gerar"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
      <CheckScopeDialog
        open={scopeOpen}
        onOpenChange={setScopeOpen}
        lotterySlug={lotterySlug}
        lotteryName={lotteryName}
        onConfirm={async ({ scope, from, until, specialScheduleId }) => {
          await saveSelected({ scope, from, until, specialScheduleId });
        }}
      />
    </Dialog>
  );
}